import { getAuthConfig } from '@/lib/auth-fetch';
import { getModuleMode } from '@/lib/feature-mode';
import { getBackendUrl } from '@/hooks/use-get-backend-url';
import type { PendingFilters, SavedFilter } from '@/contexts/RightPanelContext';

export interface SavedFilterDto {
  id: string;
  name: string;
  description?: string;
  filters: SavedFilterFiltersDto;
  createdAt: string;
  updatedAt: string;
  userId: string;
}

export interface SavedFilterFiltersDto {
  selectedCategories: string[];
  selectedStatuses: string[];
  selectedLocations: string[];
  acquisitionDateRange: { from?: string; to?: string };
  acquisitionCostMin: string;
  acquisitionCostMax: string;
  netBookValueMin: string;
  netBookValueMax: string;
  selectedAssignedTo: string[];
  selectedDepreciationMethods: string[];
  warrantyExpiryDateRange: { from?: string; to?: string };
  selectedManufacturers: string[];
}

export interface CreateSavedFilterDto {
  name: string;
  description?: string;
  filters: SavedFilterFiltersDto;
}

export interface UpdateSavedFilterDto {
  name: string;
  description?: string;
  filters: SavedFilterFiltersDto;
}

const getDataSource = (): 'demo' | 'api' => {
  const mode = getModuleMode('assets');
  if (mode === 'demo') return 'demo';
  return 'api';
};

const demoSavedFilters: SavedFilter[] = [];

const mapDtoToSavedFilter = (dto: SavedFilterDto): SavedFilter => ({
  id: dto.id,
  name: dto.name,
  description: dto.description,
  filters: {
    selectedCategories: dto.filters.selectedCategories || [],
    selectedStatuses: dto.filters.selectedStatuses || [],
    selectedLocations: dto.filters.selectedLocations || [],
    acquisitionDateRange: {
      from: dto.filters.acquisitionDateRange?.from ? new Date(dto.filters.acquisitionDateRange.from) : undefined,
      to: dto.filters.acquisitionDateRange?.to ? new Date(dto.filters.acquisitionDateRange.to) : undefined,
    },
    acquisitionCostMin: dto.filters.acquisitionCostMin || '',
    acquisitionCostMax: dto.filters.acquisitionCostMax || '',
    netBookValueMin: dto.filters.netBookValueMin || '',
    netBookValueMax: dto.filters.netBookValueMax || '',
    selectedAssignedTo: dto.filters.selectedAssignedTo || [],
    selectedDepreciationMethods: dto.filters.selectedDepreciationMethods || [],
    warrantyExpiryDateRange: {
      from: dto.filters.warrantyExpiryDateRange?.from ? new Date(dto.filters.warrantyExpiryDateRange.from) : undefined,
      to: dto.filters.warrantyExpiryDateRange?.to ? new Date(dto.filters.warrantyExpiryDateRange.to) : undefined,
    },
    selectedManufacturers: dto.filters.selectedManufacturers || [],
  },
  createdAt: new Date(dto.createdAt),
  updatedAt: new Date(dto.updatedAt),
  userId: dto.userId,
});

const mapPendingFiltersToDto = (filters: PendingFilters): SavedFilterFiltersDto => ({
  selectedCategories: filters.selectedCategories,
  selectedStatuses: filters.selectedStatuses,
  selectedLocations: filters.selectedLocations,
  acquisitionDateRange: {
    from: filters.acquisitionDateRange.from?.toISOString(),
    to: filters.acquisitionDateRange.to?.toISOString(),
  },
  acquisitionCostMin: filters.acquisitionCostMin,
  acquisitionCostMax: filters.acquisitionCostMax,
  netBookValueMin: filters.netBookValueMin,
  netBookValueMax: filters.netBookValueMax,
  selectedAssignedTo: filters.selectedAssignedTo,
  selectedDepreciationMethods: filters.selectedDepreciationMethods,
  warrantyExpiryDateRange: {
    from: filters.warrantyExpiryDateRange.from?.toISOString(),
    to: filters.warrantyExpiryDateRange.to?.toISOString(),
  },
  selectedManufacturers: filters.selectedManufacturers,
});

export class SavedFiltersService {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async buildHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const authConfig = getAuthConfig();
    if (authConfig.getAccessToken) {
      try {
        const token = await authConfig.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting access token:', error);
      }
    }

    headers['X-Instance-Id'] = authConfig.instanceId ?? 'default-instance';
    return headers;
  }

  async getSavedFilters(): Promise<SavedFilter[]> {
    if (getDataSource() === 'demo') {
      return [...demoSavedFilters];
    }

    const headers = await this.buildHeaders();
    const response = await fetch(`${this.baseUrl}/api/saved-filters`, {
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to fetch saved filters');
    }

    const dtos: SavedFilterDto[] = await response.json();
    return dtos.map(mapDtoToSavedFilter);
  }

  async getSavedFilter(filterId: string): Promise<SavedFilter | null> {
    if (getDataSource() === 'demo') {
      return demoSavedFilters.find(f => f.id === filterId) || null;
    }

    const headers = await this.buildHeaders();
    const response = await fetch(`${this.baseUrl}/api/saved-filters/${filterId}`, {
      headers,
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error('Failed to fetch saved filter');
    }

    const dto: SavedFilterDto = await response.json();
    return mapDtoToSavedFilter(dto);
  }

  async createSavedFilter(name: string, filters: PendingFilters, description?: string): Promise<SavedFilter> {
    if (getDataSource() === 'demo') {
      const newFilter: SavedFilter = {
        id: `filter-${Date.now()}`,
        name,
        description,
        filters,
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: 'demo-user',
      };
      demoSavedFilters.push(newFilter);
      return newFilter;
    }

    const headers = await this.buildHeaders();
    const body: CreateSavedFilterDto = {
      name,
      description,
      filters: mapPendingFiltersToDto(filters),
    };

    const response = await fetch(`${this.baseUrl}/api/saved-filters`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error('Failed to create saved filter');
    }

    const dto: SavedFilterDto = await response.json();
    return mapDtoToSavedFilter(dto);
  }

  async updateSavedFilter(filterId: string, name: string, filters: PendingFilters, description?: string): Promise<SavedFilter | null> {
    if (getDataSource() === 'demo') {
      const index = demoSavedFilters.findIndex(f => f.id === filterId);
      if (index === -1) return null;
      
      demoSavedFilters[index] = {
        ...demoSavedFilters[index],
        name,
        description,
        filters,
        updatedAt: new Date(),
      };
      return demoSavedFilters[index];
    }

    const headers = await this.buildHeaders();
    const body: UpdateSavedFilterDto = {
      name,
      description,
      filters: mapPendingFiltersToDto(filters),
    };

    const response = await fetch(`${this.baseUrl}/api/saved-filters/${filterId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body),
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error('Failed to update saved filter');
    }

    const dto: SavedFilterDto = await response.json();
    return mapDtoToSavedFilter(dto);
  }

  async deleteSavedFilter(filterId: string): Promise<boolean> {
    if (getDataSource() === 'demo') {
      const index = demoSavedFilters.findIndex(f => f.id === filterId);
      if (index === -1) return false;
      demoSavedFilters.splice(index, 1);
      return true;
    }

    const headers = await this.buildHeaders();
    const response = await fetch(`${this.baseUrl}/api/saved-filters/${filterId}`, {
      method: 'DELETE',
      headers,
    });

    if (response.status === 404) {
      return false;
    }

    if (!response.ok) {
      throw new Error('Failed to delete saved filter');
    }

    return true;
  }
}

export const savedFiltersService = new SavedFiltersService(
  getBackendUrl()
);
